import { useEffect } from "react";

/**
 * Attach IntersectionObserver to every element with class `.reveal`
 * mounted since last invocation. Adds `.is-visible` on entry.
 * Safe to call from multiple pages — observer is idempotent per node.
 */
export function useReveal(deps = []) {
    useEffect(() => {
        const nodes = document.querySelectorAll(".reveal:not([data-revealed])");
        if (!("IntersectionObserver" in window)) {
            nodes.forEach((n) => {
                n.classList.add("is-visible");
                n.setAttribute("data-revealed", "1");
            });
            return;
        }
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add("is-visible");
                        e.target.setAttribute("data-revealed", "1");
                        io.unobserve(e.target);
                    }
                });
            },
            { rootMargin: "0px 0px -10% 0px", threshold: 0.05 }
        );
        nodes.forEach((n) => io.observe(n));
        return () => io.disconnect();
    }, deps);
}

/**
 * Track cursor position and expose it as --mx / --my CSS variables on the
 * target element. Enables the .spotlight radial-gradient effect.
 */
export function useSpotlight(ref) {
    useEffect(() => {
        const el = ref?.current;
        if (!el) return;
        const onMove = (e) => {
            const r = el.getBoundingClientRect();
            const x = ((e.clientX - r.left) / r.width) * 100;
            const y = ((e.clientY - r.top) / r.height) * 100;
            el.style.setProperty("--mx", `${x}%`);
            el.style.setProperty("--my", `${y}%`);
        };
        el.addEventListener("pointermove", onMove);
        return () => el.removeEventListener("pointermove", onMove);
    }, [ref]);
}
